import 'whatwg-fetch';
import uuid from 'uuid';

import {TYPES} from '../constants/types';
import {getBaseUrl} from '../config';
import {ALVA_TOKEN_KEY, ALVA_IMPERSONATION_TOKEN_KEY} from 'services/auth/constants';
import {WHITE_LISTED_PATHS} from './constants';
import {ALVA_SELECTED_ORGANIZATION_KEY} from '../services/auth/constants';

const LOGOUT = '/logout';
const ERROR_PATH = '/error';

const buildUrl = (slugOrUrl) => {
    // If only slug is given, add full url.
    if (slugOrUrl.indexOf('http://') === 0 || slugOrUrl.indexOf('https://') === 0) {
        return slugOrUrl;
    }

    // Not protocol, interpreted as slug
    return getBaseUrl() + slugOrUrl;
};

export const setImpersonationToken = (token) => {
    localStorage.setItem(ALVA_IMPERSONATION_TOKEN_KEY, token);
};

export const getImpersonationToken = () => {
    return localStorage.getItem(ALVA_IMPERSONATION_TOKEN_KEY);
};

export const isImpersonating = () => {
    return !!getImpersonationToken();
};

export const clearImpersonationToken = () => {
    localStorage.removeItem(ALVA_IMPERSONATION_TOKEN_KEY);
    localStorage.removeItem(ALVA_SELECTED_ORGANIZATION_KEY);
};

export const getToken = () => {
    return localStorage.getItem(ALVA_TOKEN_KEY);
};

export const clearToken = () => {
    localStorage.removeItem(ALVA_TOKEN_KEY);
};

export const buildRequestHeaders = (contentType) => {
    let headers = {};

    if (contentType) {
        headers['Content-Type'] = contentType;
    }
    let token = getToken();

    headers['Alva-Correlation-Id'] = uuid();
    headers['Authorization'] = `Token:${token}`;

    if (window.localStorage.getItem(ALVA_SELECTED_ORGANIZATION_KEY)) {
        headers['X-Selected-Organization-Id'] = localStorage.getItem(ALVA_SELECTED_ORGANIZATION_KEY);
    }

    if (getImpersonationToken()) {
        headers['X-Impersonation-Token'] = getImpersonationToken();
    }

    return headers;
};


export const setTokenFromHeader = (response) => {
    const token = response.headers.get('Authorization');

    if (token) {
        localStorage.setItem(ALVA_TOKEN_KEY, token);
    }

    const impersonationToken = response.headers.get('X-Impersonation-Token');

    if (impersonationToken) {
        setImpersonationToken(impersonationToken);
    }

    return response;
};


const checkStatus = (response) => {
    // Check status on response. Raise error if not success
    if (response.status >= 200 && response.status < 300) {
        return response;
    }

    let error = new Error(response.statusText);

    error.response = response;
    throw error;
};

const getRedirectUrl = () => {
    return `${LOGOUT}?redirect=${window.location.pathname}`;
};

export const checkAuthentication = (response) => {
    if (response.status === 401) {

        const path = window.location.pathname;

        if (!WHITE_LISTED_PATHS.includes(path)) {
            clearImpersonationToken();
            clearToken();

            const redirectUrl = getRedirectUrl();

            window.location.replace(redirectUrl);
        }
    }
    return response;
};


export const checkServerError = (response) => {
    if (response && response.status >= 500) {
        const path = window.location.pathname;

        if (path !== ERROR_PATH) {
            window.location.replace('/error');
        }
    }

    return response;
};


const parseResponse = (response) => {
    return response.json();
};

/**
 * Ajax request that handles authentification
 *
 * @param params
 * @returns {Promise.<TResult>}
 */
function request(params) {
    // Options object
    let options = {};

    // Build URL
    let url = buildUrl(params.url);

    // Get method. Defaults to 'GET'
    options.method = params.method ? params.method.toUpperCase() : 'GET';

    // Get data. Data might be sent as text ('data') or json ('json')
    let data = (params.data ? params.data : null);

    if (params.json !== null && typeof params.json === TYPES.OBJECT) {
        data = JSON.stringify(params.json);
    }

    // Specify content type and data
    let contentType = params.contentType ? params.contentType : null;

    // Body should only have data if post or put request
    if ((options.method === 'POST' || options.method === 'PUT') && data) {
        options.body = data;
    }

    // Set headers (including token)
    options.headers = buildRequestHeaders(contentType);

    // Make request!
    return fetch(url, options)
        .then(checkServerError)
        .then(checkAuthentication)
        .then(checkStatus)
        .then(setTokenFromHeader)
        .then(parseResponse);
}


const ajax = {
    get: (params) => {
        params.method = 'GET';

        return request(params);
    },
    post: (params) => {
        params.method = 'POST';
        params.contentType = 'application/json';

        return request(params);
    },
    put: (params) => {
        params.method = 'PUT';
        params.contentType = 'application/json';

        return request(params);
    },
    delete: (params) => {
        params.method = 'DELETE';

        return request(params);
    },
    uploadFile: (url, file, data) => {
        // Build form-data object
        let formData = new FormData();

        formData.append('file', file);

        // Add additional data. Obs!! JSON must be stringified
        let key;

        for (key in data) {
            if (data.hasOwnProperty(key)) {
                if (typeof data[key] === TYPES.OBJECT) {
                    formData.append(key, JSON.stringify(data[key]));
                }
                else {
                    formData.append(key, data[key]);
                }
            }
        }

        // Build params
        const params = {
            method: 'POST',
            data: formData,
            url: url,
            contentType: false // Let browser decide
        };

        // Make request
        return request(params);
    }
};

export default ajax;
